import React, { useCallback, useEffect, useState } from 'react';
import {
  Container, Card, Button, Row, Col, Alert, Spinner,
  Form, Collapse, Badge, Modal,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { STATUS } from 'react-joyride';
import useToken from '../../hooks/useToken';
import ProgressTracker from '../shared/ProgressTracker';
import useProgressRedirect from '../../hooks/useProgressRedirect';
import { Timer } from '../shared/Timer';
import ScopeFocusInstructions from '../shared/ScopeFocusInstructions';
import PlatformTutorialTour from '../shared/PlatformTutorialTour';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSitemap, faProjectDiagram } from '@fortawesome/free-solid-svg-icons';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

/* ─── Simulation constants ──────────────────────────────────────────────── */

const FAKE_UUID       = '3fa85f64-5717-4562-b3fc-2c963f66afa6';
const FAKE_HOSTNAME   = `${FAKE_UUID}.cinemamanagement.docupen.xyz`;
const MANDATORY_LIMIT = 90 * 60;

const FAKE_WEBSITE = {
  website_name: 'Cinema Management System',
  description:
    'A web application for managing cinema operations: movies, showtimes, bookings, and user accounts.',
};

const FAKE_EXTRA_SERVICES = [
  {
    label: 'Film Production Service',
    url: `http://filmproduction.${FAKE_UUID}.cinemamanagement.docupen.xyz`,
  },
];

const FAKE_TEST_ACCOUNTS = [
  { role: 'Admin',    username: 'admin@cinema.local',  password: 'Cinema2024!' },
  { role: 'Customer', username: 'john.doe@email.com',  password: 'Popcorn123'  },
];

const FAKE_INSTRUCTIONS = {
  scope_and_focus: {
    website_name: FAKE_WEBSITE.website_name,
    summary: 'A web application for browsing movies, booking showtimes, and managing cinema accounts.',
    intro: 'Direct your testing effort toward the following features. You are free to explore the full application, but findings within these areas are the primary focus of this assessment:',
    focus_features: [
      'Authentication and account management',
      'Movie catalog and showtime scheduling',
      'Booking, checkout, and payment integration',
    ],
    note: 'This is a simulated example. During your real experiment, this section will show the scope for your assigned target.',
    ground_rules: [
      'No denial-of-service techniques',
      'No environment setup required — the application is pre-configured and ready to test',
    ],
  },
};

const FAKE_VULN_TYPES = [
  'SQL Injection',
  'Cross-Site Scripting (XSS)',
  'IDOR',
  'Broken Access Control',
  'Authentication Bypass',
  'CSRF',
  'Directory Traversal',
  'Sensitive Data Exposure',
  'Command Injection',
  'Business Logic Flaw',
];

const SEVERITY_STYLES = {
  Critical:      { bg: '#842029', text: '#fff' },
  High:          { bg: '#664d03', text: '#fff' },
  Medium:        { bg: '#084298', text: '#fff' },
  Low:           { bg: '#0f5132', text: '#fff' },
  Informational: { bg: '#495057', text: '#fff' },
};

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ['bold', 'italic', 'code'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['code-block'],
    ['clean'],
  ],
};

const isQuillEmpty = (val) =>
  !val || val === '<p><br></p>' || val.replace(/<[^>]*>/g, '').trim() === '';

const simulateDelay = () => new Promise((resolve) => setTimeout(resolve, 3000));

/* ─── Simulated website UI ──────────────────────────────────────────────── */

const emptyFinding = () => ({
  id: null,
  vulnerability_type: [],
  name: '',
  severity: '',
  affected_urls: '',
  description: '',
  payload: '',
  impact: '',
  poc_image_path: '',
  used_threat_model: false,
  threat_model_info: '',
  used_design_document: false,
  design_document_info: '',
  isNew: true,
  collapsed: false,
  saving: false,
});

function SimulatedWebsite({
  threatModelProvided,
  tourRunning,
  setTourRunning,
  tourKey,
}) {
  const [serverRunning, setServerRunning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const [overMandatory, setOverMandatory] = useState(false);
  const [overExtra, setOverExtra] = useState(false);
  const [vulnForms, setVulnForms] = useState([]);
  const [activeModal, setActiveModal] = useState(null);

  useEffect(() => {
    if (tourRunning) {
      setServerRunning(true);
      setVulnForms((fs) => (fs.length === 0 ? [emptyFinding()] : fs));
    }
  }, [tourRunning]);

  const handleJoyrideCallback = useCallback((data) => {
    const { status } = data;
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setTourRunning(false);
    }
  }, [setTourRunning]);

  /* ── server lifecycle ── */
  const handleStartServer = async () => {
    setLoading(true);
    await simulateDelay();
    setOverMandatory(false);
    setOverExtra(false);
    setServerRunning(true);
    setLoading(false);
  };

  const handleRestart = async () => {
    setLoading(true);
    await simulateDelay();
    setLoading(false);
  };

  const handleStop = async () => {
    setLoading(true);
    await simulateDelay();
    setServerRunning(false);
    setLoading(false);
  };

  const handleComplete = async () => {
    setCompleting(true);
    await simulateDelay();
    setCompleting(false);
    alert(
      '✅ In the real experiment this button saves your findings, stops the server, and advances you to the next step.'
    );
  };

  const handleSaveAll = async () => {
    setSavingAll(true);
    await simulateDelay();
    setVulnForms((fs) =>
      fs.map((f) => ({ ...f, isNew: false, id: f.id || `sim-${Math.random()}` }))
    );
    setSavingAll(false);
  };

  /* ── finding form helpers ── */
  const handleAddFinding = () => {
    if (vulnForms.length >= 20) return;
    setVulnForms((fs) => [
      ...fs,
      {
        id: null,
        vulnerability_type: [],
        name: '',
        severity: '',
        affected_urls: '',
        description: '',
        payload: '',
        impact: '',
        poc_image_path: '',
        used_threat_model: false,
        threat_model_info: '',
        used_design_document: false,
        design_document_info: '',
        isNew: true,
        collapsed: false,
        saving: false,
      },
    ]);
  };

  const handleRemoveFinding = (index) => {
    setVulnForms((fs) => fs.filter((_, i) => i !== index));
  };

  const toggleCollapse = (index) => {
    setVulnForms((fs) =>
      fs.map((f, i) => (i === index ? { ...f, collapsed: !f.collapsed } : f))
    );
  };

  const handleFindingChange = (index, field, value) => {
    setVulnForms((fs) =>
      fs.map((f, i) => (i === index ? { ...f, [field]: value } : f))
    );
  };

  const handleVulnTypeToggle = (index, vt) => {
    setVulnForms((fs) =>
      fs.map((f, i) => {
        if (i !== index) return f;
        const types = f.vulnerability_type || [];
        return {
          ...f,
          vulnerability_type: types.includes(vt)
            ? types.filter((t) => t !== vt)
            : [...types, vt],
        };
      })
    );
  };

  const handleSaveFinding = async (index) => {
    const f = vulnForms[index];
    if (!f.vulnerability_type?.length) return alert('Vulnerability Type is required.');
    if (!f.name?.trim())               return alert('Title is required.');
    if (!f.severity)                   return alert('Severity is required.');
    if (!f.affected_urls?.trim())      return alert('Affected Endpoint is required.');
    if (!f.description?.trim())        return alert('Description is required.');
    if (isQuillEmpty(f.payload))       return alert('Steps to Reproduce is required.');
    if (!f.impact?.trim())             return alert('Impact is required.');

    setVulnForms((fs) =>
      fs.map((form, i) => (i === index ? { ...form, saving: true } : form))
    );
    await simulateDelay();
    setVulnForms((fs) =>
      fs.map((form, i) =>
        i === index
          ? { ...form, saving: false, isNew: false, id: form.id || `sim-${Math.random()}` }
          : form
      )
    );
  };

  /* ── render ── */
  return (
    <>
      <PlatformTutorialTour
        key={tourKey}
        run={tourRunning}
        callback={handleJoyrideCallback}
      />

      {/* Server management card */}
      <Card className="shadow-lg border-0 mb-4">
        <Card.Body>
          <h2 className="mb-3">🔎 {FAKE_WEBSITE.website_name}</h2>
          <p className="text-muted">{FAKE_WEBSITE.description}</p>

          {serverRunning && (
            <>
              <div id="sim-timer">
                <Timer
                  initialElapsed={0}
                  limit={MANDATORY_LIMIT}
                  onLimitReached={() => setOverMandatory(true)}
                  extraLimit={MANDATORY_LIMIT + 30 * 60}
                  onExtraLimitReached={() => setOverExtra(true)}
                />
              </div>
              {overExtra && (
                <div className="mb-3 p-3 rounded" style={{ background: '#f8d7da', border: '1px solid #f5c2c7' }}>
                  <p className="mb-0 fw-bold" style={{ color: '#842029', fontSize: '1rem' }}>
                    ⚠️ You have spent an extra 30 minutes. We strongly advise you to move on to the next step.
                  </p>
                </div>
              )}
              {overMandatory && !overExtra && (
                <div className="mb-3 p-3 rounded" style={{ background: '#fff3cd', border: '1px solid #ffc107' }}>
                  <p className="mb-0 fw-bold" style={{ color: '#856404', fontSize: '1rem' }}>
                    ⏰ Mandatory testing time has passed. Feel free to proceed to the next step whenever you are ready, or keep testing.
                  </p>
                </div>
              )}
            </>
          )}

          <div id="sim-scope-instructions">
            <ScopeFocusInstructions instructions={FAKE_INSTRUCTIONS} defaultOpen />
          </div>

          {loading && !serverRunning && (
            <Alert variant="warning" className="d-flex align-items-center gap-2">
              <Spinner animation="border" size="sm" />
              <span>
                <strong>Starting up…</strong> The target environment is being initialised.
                This may take <strong>up to 3 minutes</strong> on first launch. Please wait.
              </span>
            </Alert>
          )}

          <div className="d-grid mb-3">
            <Button
              id="sim-start-btn"
              variant={serverRunning ? 'success' : 'primary'}
              onClick={handleStartServer}
              disabled={loading || serverRunning}
            >
              {loading && !serverRunning ? (
                <><Spinner animation="border" size="sm" className="me-2" />Starting Server…</>
              ) : serverRunning ? (
                '✅ Server Running'
              ) : (
                '🚀 Start Pentest Server'
              )}
            </Button>
          </div>

          {serverRunning && (
            <>
              <Alert id="sim-server-info" variant="success">
                ✅ Server started! Access it at:{' '}
                <a
                  id="sim-server-link"
                  href={`http://${FAKE_HOSTNAME}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <strong>http://{FAKE_HOSTNAME}</strong>
                </a>
              </Alert>

              <Alert id="sim-extra-services" variant="info" className="py-2">
                <strong>Additional services:</strong>
                <ul className="mb-0 mt-1">
                  {FAKE_EXTRA_SERVICES.map((s) => (
                    <li key={s.url}>
                      {s.label}:{' '}
                      <a href={s.url} target="_blank" rel="noopener noreferrer" className="alert-link">
                        {s.url}
                      </a>
                    </li>
                  ))}
                </ul>
                <small className="text-muted d-block mt-2">
                  <em>Note: These are simulation services used by the application. You do not need to pentest them.</em>
                </small>
              </Alert>

              <Alert id="sim-test-accounts" variant="secondary" className="py-2">
                <strong>🔑 Test Accounts</strong>
                <table className="table table-sm table-bordered mb-0 mt-2 bg-white">
                  <thead className="table-light">
                    <tr>
                      <th>Role</th>
                      <th>Username</th>
                      <th>Password</th>
                    </tr>
                  </thead>
                  <tbody>
                    {FAKE_TEST_ACCOUNTS.map((acc, i) => (
                      <tr key={i}>
                        <td><Badge bg="secondary">{acc.role}</Badge></td>
                        <td><code>{acc.username}</code></td>
                        <td><code>{acc.password}</code></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Alert>

              <div id="sim-server-controls" className="d-flex gap-2 mb-3">
                <Button variant="warning" onClick={handleRestart} disabled={loading}>
                  🔄 {loading ? 'Restarting...' : 'Restart'}
                </Button>
                <Button variant="danger" onClick={handleStop} disabled={loading}>
                  🛑 {loading ? 'Stopping...' : 'Stop'}
                </Button>
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {/* Project documents card */}
      <Card id="sim-project-docs-card" className="mb-4 shadow-sm">
        <Card.Body>
          <h5 className="mb-3">📄 Project Documents</h5>
          <Row>
            <Col md={threatModelProvided ? 6 : 12}>
              <Button
                id="sim-doc-btn"
                variant="primary"
                className="w-100 mb-2 mb-md-0"
                onClick={() => setActiveModal('design-docs')}
              >
                <FontAwesomeIcon icon={faSitemap} /> Open Design Document Viewer
              </Button>
            </Col>
            {threatModelProvided && (
              <Col md={6}>
                <Button
                  id="sim-tm-btn"
                  variant="success"
                  className="w-100"
                  onClick={() => setActiveModal('threat-model')}
                >
                  <FontAwesomeIcon icon={faProjectDiagram} /> Open Threat Model Viewer
                </Button>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      {/* Findings card */}
      <Card className="shadow-sm">
        <Card.Body>
          <h4 className="mb-3">📝 Submit Findings</h4>
          {vulnForms.map((finding, index) => (
            <Card key={index} id={index === 0 ? 'sim-finding-card-0' : undefined} className="mb-3 shadow-sm">
              <Card.Header
                className="d-flex justify-content-between align-items-center"
                onClick={() => toggleCollapse(index)}
                style={{ cursor: 'pointer' }}
              >
                <strong>
                  {finding.name ||
                    (Array.isArray(finding.vulnerability_type) &&
                      finding.vulnerability_type.join(', ')) ||
                    `Finding #${index + 1}`}
                </strong>
                <Button size="sm" variant="light">
                  {finding.collapsed ? '▶' : '▼'}
                </Button>
              </Card.Header>

              <Collapse in={!finding.collapsed}>
                <div>
                  <Card.Body>
                    <Form className="border rounded p-3 mb-3">
                      <Alert variant="danger" className="py-2 mb-3">
                        <small>
                          Fields marked with <i className="text-danger">*</i> are mandatory. All other fields are optional.
                        </small>
                      </Alert>

                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-2">
                            <Form.Label>Vulnerability Type <i className="text-danger">*</i></Form.Label>
                            <div id={index === 0 ? 'sim-vuln-type-0' : undefined}>
                              {FAKE_VULN_TYPES.map((vt) => (
                                <Badge
                                  key={vt}
                                  pill
                                  bg={finding.vulnerability_type?.includes(vt) ? 'primary' : 'secondary'}
                                  onClick={() => handleVulnTypeToggle(index, vt)}
                                  style={{ cursor: 'pointer', marginRight: 5, marginBottom: 5 }}
                                  className="p-2"
                                >
                                  {vt}
                                </Badge>
                              ))}
                            </div>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-2">
                            <Form.Label>Title <i className="text-danger">*</i></Form.Label>
                            <Form.Control
                              id={index === 0 ? 'sim-title-0' : undefined}
                              type="text"
                              value={finding.name}
                              onChange={(e) => handleFindingChange(index, 'name', e.target.value)}
                              placeholder="e.g. Unauthenticated Admin Access via IDOR"
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={4}>
                          <Form.Group className="mb-2">
                            <Form.Label>Severity <i className="text-danger">*</i></Form.Label>
                            <Form.Select
                              id={index === 0 ? 'sim-severity-0' : undefined}
                              value={finding.severity || ''}
                              onChange={(e) => handleFindingChange(index, 'severity', e.target.value)}
                              style={
                                finding.severity && SEVERITY_STYLES[finding.severity]
                                  ? {
                                      backgroundColor: SEVERITY_STYLES[finding.severity].bg,
                                      color: SEVERITY_STYLES[finding.severity].text,
                                      fontWeight: 'bold',
                                      border: 'none',
                                    }
                                  : {}
                              }
                            >
                              <option value="">-- Select --</option>
                              <option value="Critical">Critical</option>
                              <option value="High">High</option>
                              <option value="Medium">Medium</option>
                              <option value="Low">Low</option>
                              <option value="Informational">Informational</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={8}>
                          <Form.Group className="mb-2">
                            <Form.Label>Affected Endpoint <i className="text-danger">*</i></Form.Label>
                            <Form.Control
                              id={index === 0 ? 'sim-endpoint-0' : undefined}
                              type="text"
                              value={finding.affected_urls}
                              onChange={(e) => handleFindingChange(index, 'affected_urls', e.target.value)}
                              placeholder={`e.g. http://${FAKE_HOSTNAME}/api/movies`}
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Form.Group className="mb-2">
                        <Form.Label>Description <i className="text-danger">*</i></Form.Label>
                        <Form.Control
                          id={index === 0 ? 'sim-description-0' : undefined}
                          as="textarea"
                          rows={3}
                          value={finding.description}
                          onChange={(e) => handleFindingChange(index, 'description', e.target.value)}
                          placeholder="Briefly describe the vulnerability and how it occurs."
                        />
                      </Form.Group>

                      <Form.Group className="mb-2">
                        <Form.Label>Steps to Reproduce <i className="text-danger">*</i></Form.Label>
                        <div id={index === 0 ? 'sim-repro-0' : undefined}>
                          <ReactQuill
                            theme="snow"
                            value={finding.payload}
                            onChange={(val) => handleFindingChange(index, 'payload', val)}
                            modules={QUILL_MODULES}
                            style={{ minHeight: '140px' }}
                            placeholder="1. Navigate to..."
                          />
                        </div>
                      </Form.Group>

                      <Form.Group className="mb-2 mt-5">
                        <Form.Label>Impact <i className="text-danger">*</i></Form.Label>
                        <Form.Control
                          id={index === 0 ? 'sim-impact-0' : undefined}
                          as="textarea"
                          rows={3}
                          value={finding.impact || ''}
                          onChange={(e) => handleFindingChange(index, 'impact', e.target.value)}
                          placeholder="Describe the potential business or security impact of this vulnerability."
                        />
                      </Form.Group>

                      <Form.Group className="mb-2">
                        <Form.Label>PoC Screenshot (Max. 5MB)</Form.Label>
                        <Form.Control type="file" accept="image/*" disabled />
                        <Form.Text className="text-muted">
                          Screenshot upload is disabled in tutorial mode.
                        </Form.Text>
                      </Form.Group>

                      <div className="mb-2 p-3 rounded" style={{ background: '#f8f9fa' }}>
                        {threatModelProvided && (
                          <>
                            <Form.Check
                              type="checkbox"
                              id={`tm-check-${index}`}
                              label="Did the Threat Model information help you find this vulnerability?"
                              className="mb-2"
                              checked={finding.used_threat_model}
                              onChange={(e) =>
                                handleFindingChange(index, 'used_threat_model', e.target.checked)
                              }
                            />
                            {finding.used_threat_model && (
                              <Form.Group className="mb-2 ms-4">
                                <Form.Label className="text-muted small">
                                  How did the Threat Model help? (optional)
                                </Form.Label>
                                <Form.Control
                                  as="textarea"
                                  rows={2}
                                  value={finding.threat_model_info}
                                  onChange={(e) =>
                                    handleFindingChange(index, 'threat_model_info', e.target.value)
                                  }
                                />
                              </Form.Group>
                            )}
                          </>
                        )}

                        <Form.Check
                          type="checkbox"
                          id={`dd-check-${index}`}
                          label="Did the Design Document information help you find this vulnerability?"
                          className="mb-1"
                          checked={finding.used_design_document}
                          onChange={(e) =>
                            handleFindingChange(index, 'used_design_document', e.target.checked)
                          }
                        />
                        {finding.used_design_document && (
                          <Form.Group className="ms-4">
                            <Form.Label className="text-muted small">
                              How did the Design Document help? (optional)
                            </Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              value={finding.design_document_info}
                              onChange={(e) =>
                                handleFindingChange(index, 'design_document_info', e.target.value)
                              }
                            />
                          </Form.Group>
                        )}
                      </div>

                      <div className="d-flex justify-content-between mt-3">
                        <Button variant="danger" onClick={() => handleRemoveFinding(index)}>
                          ❌ Remove
                        </Button>
                        <Button
                          id={index === 0 ? 'sim-save-btn-0' : undefined}
                          variant="success"
                          disabled={finding.saving}
                          onClick={() => handleSaveFinding(index)}
                        >
                          {finding.saving ? (
                            <><Spinner animation="border" size="sm" className="me-1" />Saving…</>
                          ) : (
                            finding.isNew ? '💾 Add' : '💾 Save'
                          )}
                        </Button>
                      </div>
                    </Form>
                  </Card.Body>
                </div>
              </Collapse>
            </Card>
          ))}

          {vulnForms.length < 20 && (
            <div className="d-grid">
              <Button id="sim-add-finding-btn" variant="outline-primary" onClick={handleAddFinding}>
                ➕ Add New Finding
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      <div id="sim-bottom-actions" className="d-flex gap-2 my-4">
        <Button variant="outline-primary" disabled={savingAll} onClick={handleSaveAll}>
          {savingAll ? (
            <><Spinner animation="border" size="sm" className="me-2" />Saving…</>
          ) : (
            '💾 Save All Findings'
          )}
        </Button>
        <Button variant="success" disabled={completing} onClick={handleComplete}>
          {completing ? (
            <><Spinner animation="border" size="sm" className="me-2" />Completing…</>
          ) : (
            '✅ Complete this Website'
          )}
        </Button>
      </div>

      {/* Document viewer modals */}
      <Modal
        show={activeModal !== null}
        onHide={() => setActiveModal(null)}
        size="xl"
        fullscreen="lg-down"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {activeModal === 'design-docs' ? (
              <><FontAwesomeIcon icon={faSitemap} className="me-2" />Design Document Viewer — Sample</>
            ) : (
              <><FontAwesomeIcon icon={faProjectDiagram} className="me-2" />Threat Model Viewer — Sample</>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <div style={{ background: '#fff3cd', borderBottom: '1px solid #ffc107', padding: '10px 16px' }}>
            <small>
              <strong>🎓 Tutorial Mode:</strong> In the real experiment, this button opens your assigned website's{' '}
              {activeModal === 'design-docs' ? 'design documentation' : 'threat model'} viewer.
            </small>
          </div>
          <iframe
            key={activeModal}
            src={activeModal === 'design-docs' ? '/public/design-docs/sample' : '/public/threat-model/sample'}
            title={activeModal === 'design-docs' ? 'Design Document Sample' : 'Threat Model Sample'}
            style={{ width: '100%', height: '72vh', border: 'none', display: 'block' }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setActiveModal(null)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

/* ─── Platform Tutorial page ────────────────────────────────────────────── */

function PlatformTutorial() {
  const { getValidToken } = useToken();
  const navigate = useNavigate();

  useProgressRedirect('PLATFORM_TUTORIAL');

  const [threatModelProvided, setThreatModelProvided] = useState(false);
  const [tourRunning, setTourRunning] = useState(false);
  const [tourKey, setTourKey] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const authToken = await getValidToken();
        const resp = await fetch('/api/accounts/threat-model', {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
        });
        const data = await resp.json();
        setThreatModelProvided(data.threat_model_provided ?? false);
      } catch (err) {
        console.error('Failed to check threat model', err);
      } finally {
        setTourRunning(true);
      }
    })();
  }, []);

  const handleContinue = async () => {
    try {
      const authToken = await getValidToken();
      const res = await fetch('/api/player/progress', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ progress_step: 'EXPERIMENT' }),
      });
      if (res.ok) navigate('/experiment', { replace: true });
    } catch (err) {
      console.error('Failed to update progress', err);
    }
  };

  return (
    <Container className="py-5">
      <ProgressTracker currentStep="platform_tutorial" />

      <Card className="shadow-lg border-0 mb-4">
        <Card.Body>
          <h2 className="mb-2">🛠️ Platform Tutorial</h2>
          <p className="mb-0">
            Below is an interactive simulation of the experiment platform using a fictitious
            application. Try starting the server, opening the documents, and submitting a
            finding — so the real interface feels familiar when your experiment begins.
          </p>
        </Card.Body>
      </Card>

      <Alert variant="warning" className="d-flex align-items-start gap-3 mb-4">
        <span style={{ fontSize: '1.4rem', lineHeight: 1 }}>🎓</span>
        <div className="flex-grow-1">
          <strong>Tutorial Mode — no real requests are sent.</strong><br />
          This is a simulated "Cinema Management System". All buttons mimic real behaviour
          with a short delay but nothing is saved to any server. The hostname format shown
          (<code>{FAKE_HOSTNAME}</code>) matches exactly what you will see during the
          actual experiment.
        </div>
        <Button
          size="sm"
          variant="outline-secondary"
          style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          onClick={() => { setTourKey(k => k + 1); setTourRunning(true); }}
        >
          🔄 Restart Tour
        </Button>
      </Alert>

      <SimulatedWebsite
        threatModelProvided={threatModelProvided}
        tourRunning={tourRunning}
        setTourRunning={setTourRunning}
        tourKey={tourKey}
      />

      <Card className="shadow-lg border-0 mt-2">
        <Card.Body>
          <p className="mb-3 text-muted">
            Once you are comfortable with the interface, click the button below to proceed.
          </p>
          <div className="d-grid">
            <Button variant="success" size="lg" onClick={handleContinue}>
              Proceed to Targets
            </Button>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default PlatformTutorial;
